import { Action, Location } from "history";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

type PendingNavigation = Readonly<{
  action: Action;
  location: Location;
}>;

type UnsavedChangesGuard = Readonly<{
  isNavigationBlocked: boolean;
  allowNextNavigation: () => void;
  leavePage: () => void;
  stayOnPage: () => void;
}>;

export const useUnsavedChangesGuard = (
  hasUnsavedChanges: () => boolean,
  isEnabled: boolean
): UnsavedChangesGuard => {
  const history = useHistory();
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const allowNavigationRef = useRef(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    return history.block((location, action) => {
      if (allowNavigationRef.current || !hasUnsavedChangesRef.current()) {
        return;
      }

      setPendingNavigation({ action, location });
      return false;
    });
  }, [history, isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (allowNavigationRef.current || !hasUnsavedChangesRef.current()) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isEnabled]);

  const allowNextNavigation = useCallback(() => {
    allowNavigationRef.current = true;
  }, []);

  const stayOnPage = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  const leavePage = useCallback(() => {
    if (!pendingNavigation) {
      return;
    }

    allowNavigationRef.current = true;
    setPendingNavigation(null);

    if (pendingNavigation.action === "REPLACE") {
      history.replace(pendingNavigation.location);
      return;
    }

    history.push(pendingNavigation.location);
  }, [history, pendingNavigation]);

  return {
    isNavigationBlocked: pendingNavigation !== null,
    allowNextNavigation,
    leavePage,
    stayOnPage,
  };
};
